import { getCurrentInstance, useAttrs } from 'vue'

/**
 * Keys that activate a `role="button"` element, per the ARIA authoring
 * practices. Space is the literal `' '` (`event.key`), not `'Space'`.
 */
const ACTIVATION_KEYS = ['Enter', ' ']

export interface ClickActionOptions {
  /**
   * Called when the element is activated from the keyboard. Pass the same
   * thing the element's `@click` does — usually `() => emit('onClick')`.
   * Omit it when the component already owns its own keydown handler.
   */
  activate?: (event: KeyboardEvent) => void
  /** Role exposed while a click action is bound. Defaults to `button`. */
  role?: string
}

/**
 * "Does the author have an onClick processor wired to this component's schema
 * node?" — plus the ARIA/keyboard scaffolding that turns a non-semantic root
 * (`div`, `img`, `span`) into something a screen reader announces, and a
 * keyboard can reach and fire.
 *
 * Components rendering a real `<button>` do not need this; components whose
 * clickable element is a bare `div` do.
 *
 * Detection reads TWO places on purpose:
 *
 * 1. `$attrs` — `RenderComponents` binds the author's processors with
 *    `v-on="{ onClick: handler }"`, and Vue prefixes every key of a v-on
 *    object with `on`, so the schema event `onClick` arrives as the prop
 *    `onOnClick` (NOT `onClick`; same footgun `CTAButton.fireClick` documents).
 * 2. The raw `vnode.props` — a component that DECLARES `onClick` through
 *    `defineEmits` has that listener filtered out of `$attrs` (Vue's
 *    `isEmitListener` strips the `on` prefix and matches the declared emit
 *    name, so `onOnClick` → `onClick` → declared → hidden). `vnode.props` is
 *    unfiltered, so it still sees it.
 *
 * Both are read at CALL time, not captured in a computed: `instance.vnode` is
 * reassigned before every re-render, so calling these from a template binding
 * keeps the attributes live when an author adds or removes the processor in
 * the editor.
 */
export function useClickAction(options: ClickActionOptions = {}) {
  const instance = getCurrentInstance()
  const attrs = useAttrs()

  function hasClickAction(): boolean {
    if (attrs.onOnClick || attrs.onClick) return true
    const vnodeProps = instance?.vnode.props
    return !!(vnodeProps && (vnodeProps.onOnClick || vnodeProps.onClick))
  }

  /**
   * Spread onto the clickable element with `v-bind`. Empty when nothing is
   * wired, so a decorative node is never announced as an inert button.
   */
  function clickActionAttrs(): { role?: string; tabindex?: number } {
    if (!hasClickAction()) return {}
    return { role: options.role ?? 'button', tabindex: 0 }
  }

  function onActivationKeydown(event: KeyboardEvent) {
    if (!options.activate || !hasClickAction()) return
    // Only when the interactive element ITSELF has focus. A keystroke landing
    // on a descendant (a TipTap editor in a slot, an input) belongs to that
    // descendant — swallowing Space here would break typing inside it.
    if (event.target !== event.currentTarget) return
    if (!ACTIVATION_KEYS.includes(event.key)) return
    event.preventDefault()
    options.activate(event)
  }

  return { hasClickAction, clickActionAttrs, onActivationKeydown }
}
